import { useEffect, useId, useRef, type MouseEvent, type ReactNode } from 'react';
import { Surface } from '../../primitives/Surface.tsx';
import { Text } from '../../primitives/Text.tsx';
import styles from './Dialog.module.css';

/**
 * Design system § 14 — the centred modal dialog.
 *
 * A short question asked OVER the screen that raised it, so the thing being
 * decided about stays visible behind the scrim. That is the whole difference
 * between this and a state of its own: a screen replaces the context, and a
 * reader who has lost sight of what they were doing has to reconstruct it
 * before they can answer.
 *
 * It is the `Drawer`'s sibling and takes the same behaviour — scrim, focus in
 * on open, `Escape` and a scrim click to dismiss, opacity-only motion under
 * `prefers-reduced-motion` — for the case the `Drawer`'s own docstring rules
 * out: "a drawer is the wrong weight for a one-sentence confirmation."
 *
 * L2, NOT L3. § 8 gives L3 to the one hero of a screen and § 4 keeps
 * `grad/brand` away from running text; a dialog is a sentence and two
 * controls, and the hero of the screen it opens over is that screen's own
 * content. The ground is taken to full opacity for the reason the `Drawer`'s
 * is — L2's 92% white lets the screen underneath read through the words.
 *
 * WHAT IT IS NOT: a place to put anything that has somewhere better to be. A
 * dialog interrupts, so it earns its place only when the reader cannot go on
 * until they answer.
 */

interface DialogProps {
  open: boolean;
  /** The question, as a heading. */
  title: string;
  /** Dismissal — the scrim, `Escape`, and whichever action means "not this". */
  onClose: () => void;
  /** The body: a line or two, no more. */
  children: ReactNode;
  /** The decision. The primary last, the escape beside it (§ 13). */
  actions: ReactNode;
  testId?: string;
}

export function Dialog({ open, title, onClose, children, actions, testId }: DialogProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const titleId = useId();
  const bodyId = useId();

  useEffect(() => {
    if (open) ref.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  // `mousedown` on the scrim ITSELF, never on a descendant: a drag that began
  // inside the dialog and ended over the scrim is not a dismissal.
  function onScrimMouseDown(event: MouseEvent<HTMLDivElement>) {
    if (event.target === event.currentTarget) onClose();
  }

  return (
    <div className={styles.scrim} data-testid="dialog-scrim" onMouseDown={onScrimMouseDown}>
      <div className={styles.panel}>
        <Surface layer={2} radius="xl">
          <div
            ref={ref}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={bodyId}
            className={styles.frame}
            data-testid={testId}
          >
            <Text as="h2" id={titleId} variant="subheading">
              {title}
            </Text>
            <div id={bodyId} className={styles.body}>
              {children}
            </div>
            <div className={styles.actions}>{actions}</div>
          </div>
        </Surface>
      </div>
    </div>
  );
}
